import type { ReactNode } from "react";
import { Box, SvgIcon, Typography, type SvgIconProps } from "@mui/material";
import { WebshareMarkIcon } from "@/components/integrations/BrandIcons";
import KnownAppMark from "@/components/oauth/KnownAppMark";
import type { KnownApp } from "@/components/oauth/knownApps";

/**
 * The two tile sizes of the dashboard's AI agent banner (r-2140), measured off
 * the design: a connected application's tile, and the smaller one the
 * not-connected visual shows the clients in. The mark inside is half the tile.
 * Radii are in theme units (4px each).
 */
type TileSize = "large" | "small";

const TILES = {
  large: { size: 42, radius: 2.5, mark: 21 },
  small: { size: 33, radius: 1, mark: 17 },
} as const;

/** The bordered square every tile is drawn in. */
function Tile({
  size,
  testId,
  bgcolor = "background.paper",
  children,
}: {
  size: TileSize;
  testId: string;
  bgcolor?: string;
  children: ReactNode;
}) {
  const tile = TILES[size];
  return (
    <Box
      aria-hidden
      data-testid={testId}
      sx={{
        width: tile.size,
        height: tile.size,
        borderRadius: tile.radius,
        border: 1,
        borderColor: "divider",
        bgcolor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

/** The mark of a client we do not recognise: four rounded squares. */
function GenericAppIcon(props: SvgIconProps) {
  return (
    <SvgIcon viewBox="0 0 20.8 20.8" {...props}>
      <rect width="8.6" height="8.6" rx="2" />
      <rect x="12.2" width="8.6" height="8.6" rx="2" />
      <rect y="12.2" width="8.6" height="8.6" rx="2" />
      <rect x="12.2" y="12.2" width="8.6" height="8.6" rx="2" />
    </SvgIcon>
  );
}

function AppMark({
  app,
  fontSize,
}: {
  app: KnownApp | undefined;
  fontSize: number;
}) {
  return app ? (
    <KnownAppMark app={app} fontSize={fontSize} />
  ) : (
    <GenericAppIcon sx={{ fontSize, color: "text.disabled" }} />
  );
}

/** One application's tile: our mark for a name we know, the generic one otherwise. */
export function AppTile({
  app,
  size,
}: {
  app: KnownApp | undefined;
  size: TileSize;
}) {
  return (
    <Tile size={size} testId={`agent-tile-${app ?? "generic"}`}>
      <AppMark app={app} fontSize={TILES[size].mark} />
    </Tile>
  );
}

/** The applications past the third, as "+N". */
export function CounterTile({ count }: { count: number }) {
  return (
    <Tile size="large" testId="agent-tile-counter" bgcolor="grey.100">
      <Typography variant="body2" fontWeight={500} color="text.secondary">
        +{count}
      </Typography>
    </Tile>
  );
}

/** Webshare's own tile, filled with the primary colour, that the agents connect to. */
export function WebshareTile() {
  return (
    <Tile size="large" testId="agent-tile-webshare" bgcolor="primary.main">
      <WebshareMarkIcon sx={{ fontSize: 20, color: "primary.contrastText" }} />
    </Tile>
  );
}
